import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Activity,
  ChevronRight,
  Clock,
  Eye,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  RefreshCw,
  ScanLine,
  Sparkles,
  Square,
  UserRound,
  Smile,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/dermal-fillers-mumbai";
const BOTOX_PATH = "/botox-treatment-mumbai";
const RHINO_PATH = "/rhinoplasty-in-mumbai";
const LIQUID_RHINO_PATH = "/liquid-rhinoplasty-mumbai";
const FACELIFT_PATH = "/facelift-surgery-mumbai";
const NON_SURGICAL_PATH = "/non-surgical-treatments-in-mumbai";

const FILLER_FAQS = [
  {
    question: "How long do dermal fillers last?",
    answer:
      "This depends on the filler type and the area treated. Lip fillers last 6 to 9 months as the lips are a high-movement area. Cheek fillers last 9 to 18 months. Jawline and chin fillers last 12 to 18 months. Individual metabolic rate also affects duration. Patients who are very physically active or have fast metabolisms tend to break down filler more quickly than average.",
  },
  {
    question: "Are dermal fillers safe?",
    answer:
      "Yes, when administered by a trained injector with anatomical knowledge of the facial vasculature. The most serious complication of filler injection is vascular occlusion, where filler enters or compresses a blood vessel, which can cause skin necrosis or in rare cases visual disturbance. This risk is minimized by correct technique, appropriate needle or cannula selection for each area, and slow careful injection with low pressure. Choosing an injector with medical training and facial anatomy expertise is the most important safety decision in filler treatment.",
  },
  {
    question: "Can dermal fillers be dissolved if I don't like the result?",
    answer:
      "Yes, for hyaluronic acid fillers. Hyaluronidase enzyme dissolves hyaluronic acid filler rapidly, typically within 24 to 48 hours, restoring the pre-treatment baseline. This makes hyaluronic acid fillers the most appropriate choice for patients having their first filler treatment or trying a new area, as the treatment is reversible if the result is unsatisfactory. Calcium hydroxylapatite and poly-L-lactic acid fillers are not reversible.",
  },
  {
    question: "Will dermal fillers make me look unnatural?",
    answer:
      "An unnatural result comes from excessive volume, incorrect filler type for the area, or poor placement technique. Natural results come from proportional planning, appropriate filler selection, and restrained volume matched to the patient's facial structure. Dr. Jain's approach is to enhance the patient's own features within their facial proportions rather than apply a standard volume protocol. Patients who are concerned about unnatural results are shown the planned treatment areas and discuss expected volumes before any injection.",
  },
  {
    question: "Do dermal fillers hurt?",
    answer:
      "Most patients experience mild discomfort during injection. Topical numbing cream applied for 15 to 20 minutes before the procedure reduces sensitivity significantly. Most modern hyaluronic acid fillers also contain lidocaine within the gel, which provides additional anesthesia from the moment of injection. Lips are the most sensitive area for filler injection. Dental nerve blocks can be used for lip filler in patients who are particularly sensitive.",
  },
  {
    question: "Can I exercise or drink alcohol after filler treatment?",
    answer:
      "Both should be avoided for 24 hours after treatment. Exercise increases blood flow and body temperature, which worsens post-injection swelling and bruising. Alcohol dilates blood vessels and has a blood-thinning effect that also worsens bruising. Avoiding both for 24 hours produces a more comfortable and faster-settling result.",
  },
  {
    question: "What is the difference between fillers and a facelift?",
    answer:
      "Fillers restore volume and smooth surface folds. They do not lift descended facial tissue, remove excess skin, or repair separated muscles. A facelift repositions descended soft tissue, removes excess skin, and produces structural changes that fillers cannot replicate. Fillers are appropriate for mild to moderate aging where volume loss is the dominant concern. A facelift is appropriate when significant tissue descent, jowling, and skin laxity are the dominant concerns. Some patients benefit from fillers used alongside facelift to restore volume that surgery does not address.",
  },
  {
    question: "How many syringes of filler do I need?",
    answer:
      "This depends on the areas being treated, the degree of volume loss, and the patient's facial proportions. A single syringe addresses one area modestly. Most comprehensive facial treatments involving multiple areas use 2 to 4 syringes distributed across the face. Starting conservatively with less filler and adding at a follow-up session is a safer approach than overfilling at the first session. Dr. Jain estimates the appropriate volume during consultation based on the facial assessment.",
  },
];

const faceBodyLocation = { "@type": "AnatomicalStructure", name: "Face" };
const plasticSpecialty = "https://schema.org/PlasticSurgery";
const percutaneousType = "https://schema.org/PercutaneousProcedure";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Dermal filler injection",
      description:
        "Minimally invasive injectable treatment to restore facial volume, soften folds, and refine contours using hyaluronic acid or other approved fillers under medical supervision.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Lip filler treatment",
      description: "Hyaluronic acid filler to enhance lip volume, definition, and symmetry with anatomically informed technique.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Cheek filler treatment",
      description: "Mid-face volumizing filler to restore cheek structure and indirect support for nasolabial folds.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Jawline filler treatment",
      description: "Structural filler along the mandibular border to improve lower-face definition and profile.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Tear trough filler treatment",
      description: "Careful under-eye volumization to soften hollows; requires precise product choice and injection plane.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: FILLER_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function DermalFillersJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical Treatments", href: NON_SURGICAL_PATH },
    { name: "Dermal Fillers", href: "/dermal-fillers-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Dermal Fillers</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Non-Surgical Facial Volume &amp; Contouring by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Dermal Fillers" icon="✨" size="hero"  imageSrc={HERO.dermalFillers} imageAlt="Dermal fillers"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatAreFillersSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-are-fillers-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What are Dermal Fillers?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Dermal fillers are injectable gels placed beneath the skin to restore lost facial volume, smooth wrinkles and folds, and enhance facial contours. The most commonly used fillers are made of hyaluronic acid, a substance that occurs naturally in the body and is responsible for keeping skin hydrated and plump. Hyaluronic acid fillers are temporary, lasting 6 to 18 months depending on the area treated, and are completely reversible if needed using an enzyme called hyaluronidase.
            </p>
            <p>
              Fillers produce results immediately after injection, which is what distinguishes them from treatments like botulinum toxin that take days to work. Swelling from the injection process settles over 2 to 3 days and the final result is visible from that point. The procedure takes 20 to 40 minutes in a clinic setting with no hospital admission. Most patients return to normal daily activity the same day.
            </p>
            <p>
              Dr. Vicky Jain performs dermal filler treatment for facial volume restoration, lip enhancement, cheek and jawline contouring, tear trough correction, and non-surgical rhinoplasty. The treatment plan is designed around each patient's facial proportions and specific concerns rather than a standard volume applied to every patient.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Dermal Filler Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Dermal filler placement is a medical procedure that requires anatomical knowledge of the facial vascular structures. The most serious complication of filler injection, vascular occlusion where filler inadvertently enters or compresses a blood vessel, is rare but can cause skin necrosis or, in the most severe cases, visual disturbance. This risk is minimized by knowing where the danger zones are in each facial area, using the correct injection plane and technique for each location, aspirating before injecting where appropriate, and injecting slowly with low pressure. These are not optional best practices. They are the technical requirements for safe filler administration.
              </p>
              <p>
                Beyond safety, the aesthetic result depends on proportional planning before the first injection is placed. Dr. Jain assesses the facial structure, existing volume distribution, and the patient's natural proportions before deciding where to place filler and how much to use. The goal is enhancement that looks natural within the patient's own facial context, not a standardized volume applied identically to every patient. Patients who have had fillers elsewhere that look overdone or unnatural typically have been treated with excessive volume or filler placed in the wrong anatomical plane. Dr. Jain dissolves existing filler when it is distorting the anatomy before adding new treatment.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AreasTreatedSection() {
  const cards = [
    {
      title: "Lip Enhancement",
      icon: Smile,
      copy: "Hyaluronic acid filler adds volume, definition, and symmetry to the lips. Natural lip enhancement adds to the patient's existing lip shape without producing the overfilled, unnatural result that comes from excessive volume. Lip filler lasts 6 to 9 months. Patients who want very subtle results or who have never had filler start with conservative volumes that can be built upon at a follow-up session.",
    },
    {
      title: "Cheek Augmentation",
      icon: Sparkles,
      copy: "Mid-face volume loss with age causes the face to look tired and aged. Filler placed in the mid-face lifts the cheeks, reduces nasolabial fold depth indirectly, and restores the three-dimensional structure that makes faces look young and healthy. Results last 9 to 18 months depending on filler type and metabolic rate.",
    },
    {
      title: "Jawline Contouring",
      icon: Square,
      copy: "A defined jawline improves the lower face profile and sharpens the transition from face to neck. Filler along the jaw creates structural definition without surgery. Results last 12 to 18 months. Often combined with chin filler for a complete lower face result.",
    },
    {
      title: "Chin Enhancement",
      icon: UserRound,
      copy: "Chin filler adds forward projection and improves the profile balance between the nose and chin. An option for patients who want to assess the change before committing to chin implant surgery, or for those who want a more modest improvement than surgery provides. Results last 12 to 18 months.",
    },
    {
      title: "Tear Trough",
      icon: Eye,
      copy: "The tear trough is the hollow between the lower eyelid and the cheek that creates a tired, sunken under-eye appearance. Filler placed carefully in this area reduces the hollow and the associated dark shadow. One of the most technically demanding filler areas due to the thin overlying skin and proximity to the eye. Appropriate filler selection and placement technique are critical here.",
    },
    {
      title: "Nasolabial Folds",
      icon: Activity,
      copy: "Deep smile lines from the nose to the corners of the mouth are reduced by placing filler in the fold or in the mid-face above it. Direct fold filling alone can look unnatural if the underlying volume loss causing the fold is not also addressed. Dr. Jain treats nasolabial folds as part of an overall mid-face assessment rather than in isolation.",
    },
    {
      title: "Non-Surgical Rhinoplasty",
      icon: ScanLine,
      copy: "Filler placed along the nasal bridge or tip can camouflage a dorsal hump, improve tip definition, or lift a drooping tip. Results are temporary, lasting 9 to 12 months, and fillers cannot reduce the size of the nose or correct functional breathing problems. Appropriate for patients with minor concerns or those wanting to preview a change before surgical rhinoplasty.",
      learnMoreHref: LIQUID_RHINO_PATH,
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20" data-testid="areas-treated-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Areas Treated with Dermal Fillers</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
              {card.learnMoreHref ? (
                <p className="mt-4">
                  <Link
                    to={card.learnMoreHref}
                    className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors text-sm"
                  >
                    Learn more about liquid rhinoplasty
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FILLER_TYPES = [
  {
    title: "Hyaluronic Acid Fillers",
    body: "The most widely used category worldwide. Available in multiple consistencies from thin and fluid for delicate areas like the lips and tear trough, to thick and cohesive for structural areas like the cheeks and jawline. Results last 6 to 18 months depending on the product and the area treated. Fully reversible with hyaluronidase enzyme injection if needed. The combination of effectiveness, reversibility, and safety record makes hyaluronic acid fillers the first choice for most facial filler applications.",
  },
  {
    title: "Calcium Hydroxylapatite Fillers",
    body: "Thicker, more robust fillers that provide structural support in deeper tissue planes. Suitable for cheek augmentation, jawline definition, and chin enhancement. Last 12 to 18 months. Not reversible with an enzyme. Used selectively for patients where the clinical indication matches the filler properties.",
  },
  {
    title: "Poly-L-Lactic Acid Fillers",
    body: "Work differently from volume-adding fillers. Stimulate the patient's own collagen production over several months, gradually improving skin quality and volume. Results appear slowly over 3 to 6 months after injection. More appropriate for diffuse facial volume loss than for specific structural augmentation. Requires multiple sessions.",
  },
  {
    title: "Permanent Fillers",
    body: "Not recommended. Permanent fillers cannot be dissolved if the result is unsatisfactory or if complications develop. As the face ages around a permanent filler, the treated area looks increasingly discordant with surrounding tissue. The risk-benefit profile does not favor permanent fillers for facial aesthetic use.",
  },
];

function TypesOfFillersSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-of-fillers-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Types of Dermal Fillers</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {FILLER_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_BOTOX_ROWS = [
  {
    label: "What It Does",
    fillers: "Adds volume and restores structural contour. Fills wrinkles and folds by replacing tissue volume.",
    toxin: "Relaxes facial muscles that cause dynamic wrinkles. Does not add volume.",
  },
  {
    label: "Best For",
    fillers: "Volume loss, static wrinkles, lip enhancement, cheek and jawline contouring.",
    toxin: "Expression lines such as forehead lines, frown lines, and crow's feet caused by muscle movement.",
  },
  {
    label: "Onset of Results",
    fillers: "Immediate. Swelling settles over 2 to 3 days to reveal the final result.",
    toxin: "Gradual. Full effect visible at 10 to 14 days after injection.",
  },
  {
    label: "Duration",
    fillers: "6 to 18 months depending on filler type and area treated.",
    toxin: "3 to 6 months.",
  },
  {
    label: "Reversibility",
    fillers: "Hyaluronic acid fillers can be dissolved with hyaluronidase.",
    toxin: "Not reversible. Effect wears off naturally over 3 to 6 months.",
  },
];

function VsBotoxSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20" data-testid="vs-botox-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Dermal Fillers vs. Botulinum Toxin: Understanding the Difference
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Dermal Fillers
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={BOTOX_PATH} className="hover:text-gold transition-colors">
                    Botulinum Toxin
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_BOTOX_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.fillers}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.toxin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Dermal fillers and botulinum toxin address different aspects of facial aging and are frequently combined in the same session for patients who have both volume loss and muscle-driven wrinkles. A comprehensive facial rejuvenation plan may include both treatments in a coordinated approach.
        </p>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Dermal Filler Treatment is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure takes 20 to 40 minutes depending on the number of areas being treated. Dr. Jain first examines the face in multiple positions, standing and sitting, and marks the planned injection sites based on the facial assessment. A topical numbing cream is applied to the treatment areas for 15 to 20 minutes before any injection.
            </p>
            <p>
              Fillers are injected using fine needles or blunt-tipped cannulas depending on the area being treated. Cannulas are preferred in areas where large blood vessels are present, such as the cheeks and tear trough, because the blunt tip is less likely to pierce a vessel than a sharp needle. Needles are used where precise point placement is needed, such as the lip border.
            </p>
            <p>
              After injection, the treated area is massaged gently to distribute the filler evenly and the result is assessed. Ice is applied to reduce initial swelling. Patients leave the clinic immediately after treatment. Final results are visible at 2 to 3 days when injection swelling has resolved.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsTimelineSection() {
  const milestones = [
    {
      period: "Immediately After",
      description:
        "Visible improvement is present immediately after injection. Swelling from the injection process means the result at this stage is slightly more than the final outcome.",
      icon: Sparkles,
    },
    {
      period: "Days 2 to 3",
      description: "Swelling resolves. The final result is visible. Any bruising from injection is settling.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description: "The filler has fully integrated into the tissue. The result is stable and represents the expected outcome for this treatment.",
      icon: Clock,
    },
    {
      period: "6 to 18 Months",
      description:
        "Results gradually reduce as the hyaluronic acid is naturally broken down by the body. Maintenance treatment at this interval sustains the improvement.",
      icon: RefreshCw,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="results-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Results Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-white/20" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-white/45" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-white/20" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-white/45 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Who Is a Good Candidate for Dermal Fillers?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults with mild to moderate signs of facial aging including volume loss, deepening folds, loss of lip definition, or reduced jawline and cheek structure who want non-surgical improvement. Patients who want to trial a change before committing to surgery, such as those considering rhinoplasty or chin augmentation, are also well-suited to fillers as a temporary preview. Patients with realistic expectations about the degree and duration of improvement from fillers, understanding they are temporary and require maintenance, get the most consistent satisfaction from the treatment.
            </p>
            <p>
              Fillers are not appropriate for patients who are pregnant or breastfeeding, patients with active infection or inflammation at the planned injection site, patients with certain autoimmune conditions, or patients who have had previous filler in the same area that has not yet fully dissolved. Patients with very significant facial aging, extensive skin laxity, or structural concerns that require surgery will not achieve adequate correction from fillers alone. Dr. Jain advises directly when a patient's concern is better addressed surgically than with fillers, rather than providing a temporary solution for a problem that requires a structural answer.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Cosmetic" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {FILLER_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`filler-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Dermal Filler Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your facial proportions, volume distribution, and specific concerns to design a filler plan that enhances your natural features. The planned treatment areas, filler types, and expected volumes are discussed before any injection. You leave with a clear understanding of what the treatment involves and what the result will look like.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Botulinum Toxin",
      description: "Muscle relaxation for dynamic expression lines to complement filler volume treatment.",
      href: BOTOX_PATH,
    },
    {
      name: "Rhinoplasty",
      description: "Permanent nasal reshaping for patients who want results beyond what non-surgical rhinoplasty with fillers achieves.",
      href: RHINO_PATH,
    },
    {
      name: "Facelift",
      description: "Structural tissue lifting for patients with facial aging beyond what fillers can address non-surgically.",
      href: FACELIFT_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function DermalFillersPage() {
  return (
    <>
      <SEO
        title="Dermal Fillers | Dr. Vicky Jain"
        description="Restore facial volume, smooth wrinkles, and enhance contours without surgery. Dr. Vicky Jain provides dermal filler treatment. Book your consultation today."
        canonical="/dermal-fillers-mumbai"
      />
      <DermalFillersJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatAreFillersSection />
      <DrJainApproachSection />
      <AreasTreatedSection />
      <TypesOfFillersSection />
      <VsBotoxSection />
      <HowPerformedSection />
      <ResultsTimelineSection />
      <GoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
